import { Skeleton, styled, SxProps, Theme, Typography } from '@mui/material';
import React from 'react';
import StandardInput from './variants/StandardInput';
import SheetInput from './variants/SheetInput';
import FilledInput from './variants/FilledInput';
import { AdornmentPosition } from './variants/Adornment';
import {
    ResizeObserverCallback,
    useResizeObserverWithElement,
} from 'components/hooks/useResizeObserver';
import { IconButton } from 'components/IconButton';
import Symbol, { SymbolProps } from 'components/Symbol';
import {
    DEFAULT_FIELD_CONTENT_HEIGHT,
    Field,
} from 'components/Widget/Form/Form';

export const DEFAULT_TEXT_FIELD_HEIGHT = DEFAULT_FIELD_CONTENT_HEIGHT;
export { Label } from 'components/Widget/Form/Form';

const Base = styled('div', {
    shouldForwardProp: prop =>
        prop !== 'width' &&
        prop !== 'minWidth' &&
        prop !== 'maxWidth' &&
        prop !== 'fullWidth' &&
        prop !== 'sx',
})<{
    width: string | number;
    minWidth?: string | number;
    maxWidth: string | number;
    fullWidth?: boolean;
}>(({ width, minWidth, maxWidth, fullWidth }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: fullWidth ? '100%' : width,
    minWidth: minWidth,
    maxWidth: maxWidth,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
}));

const InputWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
}));

export interface TextFieldProps extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange'
> {
    // Core input properties
    value?: React.InputHTMLAttributes<HTMLInputElement>['value'];
    onChange?: (
        value: string,
        event: React.ChangeEvent<HTMLInputElement>
    ) => void;
    type?: React.HTMLInputTypeAttribute;

    // Behavior
    variant?: 'standard' | 'filled' | 'sheet';
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    loading?: boolean;

    // Labeling & Display
    label?: string;
    helperText?: React.ReactNode;
    placeholder?: string;
    description?: string; // info icon with tooltip

    // Adornments (UI elements inside or outside the input)
    startAdornment?: string | React.ReactNode;
    startAdornmentOnClick?: () => void; // Only used if startAdornment is a string
    startAdornmentProps?: Partial<SymbolProps>;
    startAdornmentSx?: SxProps<Theme>;
    endAdornment?: string | React.ReactNode;
    endAdornmentOnClick?: () => void; // Only used if endAdornment is a string
    endAdornmentProps?: Partial<SymbolProps>;
    endAdornmentSx?: SxProps<Theme>;
    endButton?: React.ReactNode; // Outside the input field

    // Layout & Sizing
    width?: string | number;
    minWidth?: string | number;
    maxWidth?: string | number;
    height?: number;
    fullWidth?: boolean;

    // Styling Customization
    backgroundColor?: string;
    sx?: SxProps<Theme>;
    variantSx?: SxProps<Theme>; // Styling for the base component of the variant
    inputSx?: SxProps<Theme>; // Styling for the input element

    // Base-level event handlers
    onBaseClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onBaseMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
    onBaseMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            // Core input properties
            value,
            onChange,
            type = 'text',

            // Behavior
            variant = 'standard',
            disabled,
            required,
            error,
            loading,

            // Labeling & Display
            label,
            helperText,
            placeholder,
            description,

            // Adornments (inside & outside the input)
            startAdornment: _startAdornment,
            startAdornmentOnClick,
            startAdornmentProps,
            startAdornmentSx,
            endAdornment: _endAdornment,
            endAdornmentOnClick,
            endAdornmentProps,
            endAdornmentSx,
            endButton,

            // Layout & Sizing
            width = 300,
            minWidth,
            maxWidth = '100%',
            height = DEFAULT_TEXT_FIELD_HEIGHT,
            fullWidth,

            // Styling Customization
            backgroundColor,
            sx,
            variantSx,
            inputSx,

            // Base event handlers
            onBaseClick,
            onBaseMouseEnter,
            onBaseMouseLeave,

            // Event handling and all other default props
            ...props
        }: TextFieldProps,
        ref
    ) => {
        const [startAdornmentElement, setStartAdornmentElement] =
            React.useState<HTMLDivElement | null>(null);
        const [endAdornmentElement, setEndAdornmentElement] =
            React.useState<HTMLDivElement | null>(null);

        const startAdornmentWidth = useAdornmentWidthWithElement(
            startAdornmentElement,
            'start'
        );
        const endAdornmentWidth = useAdornmentWidthWithElement(
            endAdornmentElement,
            'end'
        );

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange?.(event.target.value, event);
        };

        const SelectedVariant = React.useMemo(() => {
            switch (variant) {
                case 'filled':
                    return FilledInput;
                case 'sheet':
                    return SheetInput;
                case 'standard':
                default:
                    return StandardInput;
            }
        }, [variant]);

        const resolveAdornment = (
            adornment: string | React.ReactNode | undefined,
            onClick?: () => void,
            symbolProps?: Partial<SymbolProps>
        ) => {
            if (!adornment) {
                return null;
            }
            if (typeof adornment === 'string') {
                if (onClick) {
                    const {
                        color: _,
                        size: inputSize,
                        ...props
                    } = symbolProps || {};
                    let size = inputSize;
                    if (size === 'xl' || size === '2xl') {
                        size = 'lg';
                    }

                    return (
                        <IconButton
                            icon={adornment}
                            onClick={e => {
                                onClick();
                                e.stopPropagation();
                            }}
                            size={size || 'xs'}
                            variant='text'
                            disabled={disabled}
                            {...props}
                        />
                    );
                }
                return (
                    <Symbol
                        symbol={adornment}
                        size='sm'
                        color='grey'
                        {...symbolProps}
                    />
                );
            }
            return adornment;
        };

        const startAdornment = resolveAdornment(
            _startAdornment,
            startAdornmentOnClick,
            startAdornmentProps
        );

        const endAdornment = resolveAdornment(
            _endAdornment,
            endAdornmentOnClick,
            endAdornmentProps
        );

        return (
            <Base
                width={width}
                minWidth={minWidth}
                maxWidth={maxWidth}
                fullWidth={fullWidth}
                sx={sx}>
                <Field label={label} required={required} contentHeight={height}>
                    <InputWrapper>
                        {loading && variant !== 'sheet' ? (
                            <Skeleton
                                variant='rounded'
                                width={'100%'}
                                height={height}
                            />
                        ) : (
                            <SelectedVariant
                                {...props} // Spread first to allow explicit props to override defaults
                                ref={ref}
                                // Core input properties
                                value={value}
                                onChange={handleChange}
                                type={type}
                                // Behavior
                                disabled={disabled}
                                required={required}
                                error={error}
                                height={height}
                                // Adornments (inside the input)
                                startAdornment={startAdornment}
                                startAdornmentWidth={startAdornmentWidth}
                                startAdornmentRef={setStartAdornmentElement}
                                startAdornmentSx={startAdornmentSx}
                                endAdornment={endAdornment}
                                endAdornmentWidth={endAdornmentWidth}
                                endAdornmentRef={setEndAdornmentElement}
                                endAdornmentSx={endAdornmentSx}
                                // Styling
                                backgroundColor={backgroundColor}
                                sx={variantSx}
                                inputSx={inputSx}
                                // Labeling & Display
                                placeholder={placeholder}
                                // Event handlers for <Base>
                                onBaseClick={onBaseClick}
                                onBaseMouseEnter={onBaseMouseEnter}
                                onBaseMouseLeave={onBaseMouseLeave}
                            />
                        )}
                        {description && (
                            <Symbol
                                symbol='info'
                                color='textSecondary'
                                size='md'
                                tooltip={description}
                            />
                        )}
                        {helperText && variant === 'sheet' ? (
                            <Symbol
                                symbol='error'
                                color='error'
                                size='lg'
                                tooltip={helperText}
                                sx={{
                                    paddingRight: 1,
                                }}
                            />
                        ) : (
                            endButton
                        )}
                    </InputWrapper>
                </Field>
                {helperText && variant !== 'sheet' && (
                    <Typography
                        variant='caption'
                        color={error ? 'error' : 'textSecondary'}
                        sx={{
                            paddingLeft: 2,
                            paddingTop: 0.25,
                            userSelect: 'none',
                        }}
                        component={'div'}>
                        {helperText}
                    </Typography>
                )}
            </Base>
        );
    }
);

TextField.displayName = 'TextField';

export default TextField;

const useAdornmentWidthWithElement = (
    element: Element | null,
    position: AdornmentPosition
) => {
    const [size, setSize] = React.useState<number>(0);

    const callback: ResizeObserverCallback = React.useCallback(
        entry => {
            const rect = entry.target.getBoundingClientRect();
            let total = rect.width;

            const computedStyle = window.getComputedStyle(entry.target);
            if (position === 'start') {
                total += parseFloat(computedStyle.left) || 0;
            } else {
                total += parseFloat(computedStyle.right) || 0;
            }
            setSize(total);
        },
        [position]
    );

    useResizeObserverWithElement(element, callback, () => {
        setSize(0);
    });

    return size;
};
